import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { lookupStoredReferral, getStoredReferral, clearStoredReferral } from "../lib/referral";
import { ArrowRight, Award, Check, CreditCard, Gift, X } from "lucide-react";

const details = {
  consult_free: ["30-minute strategy session","Direct discussion with senior consultant","Complimentary — no obligation"],
  restoration_starter: ["Full 3-bureau credit report analysis","Identification of disputable items","Monthly dispute campaign","Bureau communication management","30-day progress report"],
  restoration_pro: ["Everything in Starter","Comprehensive multi-round dispute campaign","Personalized credit-building roadmap","Authorized-user strategy","Monthly coaching calls"],
};

function ReferralBanner({ referral, onRemove }) {
  if (!referral) return null;
  return (
    <div className="bg-[#4F5D1E]/15 border border-[#4F5D1E]/40 rounded-2xl p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="referral-banner">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#4F5D1E]/30 flex items-center justify-center">
          <Gift className="w-5 h-5 text-[#4F5D1E]" />
        </div>
        <div>
          <div className="font-display text-lg text-[#1B211A]">${referral.discount_usd.toFixed(0)} off applied</div>
          <div className="text-sm text-[#1B211A]/70" style={{ fontFamily: "Inter, sans-serif" }}>
            Referred by <strong className="text-[#546643]">{referral.referrer_first_name}</strong> · code <span className="font-mono">{referral.code}</span>
          </div>
        </div>
      </div>
      <button onClick={onRemove} className="text-[#1B211A]/50 hover:text-[#1B211A] p-2" data-testid="referral-remove">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState("");
  const [referral, setReferral] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data));
    lookupStoredReferral().then(setReferral);
  }, []);

  const startCheckout = async (svc) => {
    // Subscriptions require a logged-in account so we can attach the Stripe
    // customer + subscription to the user record. Send guests to login first.
    if (svc.kind === "subscription" && !user) {
      navigate("/login", { state: { from: "/services" } });
      return;
    }
    setLoading(svc.id);
    try {
      if (svc.kind === "subscription") {
        const { data } = await api.post("/subscriptions/checkout", {
          package_id: svc.id,
          origin_url: window.location.origin,
        });
        window.location.href = data.url;
        return;
      }
      const referral_code = getStoredReferral() || undefined;
      const { data } = await api.post("/payments/checkout-session", {
        package_id: svc.id,
        origin_url: window.location.origin,
        referral_code,
      });
      window.location.href = data.url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" && detail
        ? detail
        : (e.message || "Unable to start checkout. Please try again.");
      alert(msg);
      setLoading("");
    }
  };

  const removeReferral = () => { clearStoredReferral(); setReferral(null); };

  return (
    <>
      <section className="hero-radial py-24 relative z-[2]">
        <div className="container-if">
          <div className="font-mono-label text-[var(--if-gold)] mb-3">What We Do</div>
          <h1 className="font-display text-[48px] md:text-[80px] leading-none mb-5">
            Our <span className="italic text-[var(--if-gold)]">services</span>.
          </h1>
          <p className="text-lg text-[rgba(35,38,28,0.7)] max-w-2xl">
            Every service we offer is designed with one goal: getting you approved.
            Here's exactly what we do, how we do it, and what it costs.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-if">
          <ReferralBanner referral={referral} onRemove={removeReferral} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => {
              const isSub = s.kind === "subscription";
              const showDiscount = !isSub && referral && s.amount > 0;
              const discounted = showDiscount ? Math.max(0.5, s.amount - referral.discount_usd) : s.amount;
              const isPro = s.id === "restoration_pro";
              return (
                <div key={s.id} className={`card-dark p-8 flex flex-col relative ${isPro ? "border-[var(--if-gold)]/60 ring-1 ring-[var(--if-gold)]/30" : ""}`} data-testid={`svc-card-${s.id}`}>
                  {isPro && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--if-gold)] text-[#F5F4EF] text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                      Most Popular
                    </div>
                  )}
                  <h3 className="font-display text-2xl mb-2">{s.name}</h3>
                  <div className="flex items-baseline gap-2 mb-4">
                    {s.amount > 0 ? (
                      <>
                        {showDiscount && (
                          <span className="text-lg text-white/40 line-through" style={{ fontFamily: "Inter, sans-serif" }}>${s.amount.toFixed(2)}</span>
                        )}
                        <span className="font-display text-4xl text-[var(--if-gold)]">${(discounted).toFixed(2)}</span>
                        <span className="text-sm text-[rgba(35,38,28,0.5)]">{isSub ? "/ month" : "one-time"}</span>
                      </>
                    ) : (
                      <span className="font-display text-3xl text-[var(--if-gold)]">Free</span>
                    )}
                  </div>
                  <p className="text-sm text-[rgba(35,38,28,0.7)] leading-relaxed mb-5">{s.description}</p>
                  <ul className="flex flex-col gap-2 mb-7 flex-1">
                    {(details[s.id] || []).map((d) => (
                      <li key={d} className="flex items-start gap-2 text-sm text-[rgba(35,38,28,0.8)]">
                        <Check className="w-4 h-4 text-[var(--if-gold)] mt-0.5 shrink-0" /> {d}
                      </li>
                    ))}
                  </ul>
                  {s.amount > 0 ? (
                    <button
                      onClick={() => startCheckout(s)}
                      disabled={loading === s.id}
                      className="btn-gold w-full"
                      data-testid={`svc-buy-${s.id}`}
                    >
                      {loading === s.id ? "Redirecting…" : <>{isSub ? (user ? "Subscribe" : "Sign in to subscribe") : "Secure This Package"} <CreditCard className="w-4 h-4" /></>}
                    </button>
                  ) : (
                    <Link to="/booking" className="btn-outline w-full" data-testid={`svc-book-${s.id}`}>
                      Book Free Consultation <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                  {isSub && (
                    <p className="text-[10px] text-white/40 mt-3 text-center" style={{ fontFamily: "Inter, sans-serif" }}>
                      Cancel anytime from your dashboard.
                    </p>
                  )}
                </div>
              );
            })}

            {/* Ebook card */}
            <div className="card-dark p-8 flex flex-col relative" data-testid="svc-card-ebook">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#23261C] text-[#F5F4EF] text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                Instant Download
              </div>
              <h3 className="font-display text-2xl mb-2">From Denied to Approved</h3>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-display text-4xl text-[var(--if-gold)]">$4.99</span>
                <span className="text-sm text-[rgba(35,38,28,0.5)]">one-time</span>
              </div>
              <p className="text-sm text-[rgba(35,38,28,0.7)] leading-relaxed mb-5">
                The complete Credit Restoration Roadmap ebook — the same step-by-step system we use with paying clients, delivered instantly.
              </p>
              <ul className="flex flex-col gap-2 mb-7 flex-1">
                {["The exact denied-to-approved roadmap", "Read your report like an underwriter", "Which negative items to attack first", "Fastest credit-building moves", "Personal licensed copy, emailed instantly"].map((d) => (
                  <li key={d} className="flex items-start gap-2 text-sm text-[rgba(35,38,28,0.8)]">
                    <Check className="w-4 h-4 text-[var(--if-gold)] mt-0.5 shrink-0" /> {d}
                  </li>
                ))}
              </ul>
              <Link to="/ebook" className="btn-gold w-full" data-testid="svc-buy-ebook">
                Get the Ebook <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section dark-scope bg-white">
        <div className="container-if max-w-3xl mx-auto">
          <div className="card-dark p-8 md:p-10 border-[var(--if-gold)]/40 relative overflow-hidden" data-testid="guarantee-banner">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--if-gold)]/5 rounded-full blur-3xl pointer-events-none" />
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 relative">
              <div className="w-14 h-14 rounded-full bg-[var(--if-gold)]/15 border border-[var(--if-gold)]/40 flex items-center justify-center shrink-0">
                <Award className="w-7 h-7 text-[var(--if-gold)]" />
              </div>
              <div className="flex-1">
                <div className="font-mono-label text-[var(--if-gold)] mb-2">90-Day Money-Back Guarantee</div>
                <h3 className="font-display text-2xl md:text-3xl mb-2">
                  We deliver, or you don't pay.
                </h3>
                <p className="text-[rgba(35,38,28,0.75)]" style={{ fontFamily: "Inter, sans-serif" }}>
                  If we don't get at least <strong className="text-white">one negative item deleted</strong> from your credit report within 90 days, you get a <strong className="text-white">full refund</strong> — no questions, no fine print.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section dark-scope bg-white">
        <div className="container-if text-center max-w-2xl mx-auto">
          <h2 className="font-display text-4xl md:text-5xl mb-5">Not sure which is right for you?</h2>
          <p className="text-[rgba(35,38,28,0.7)] mb-8">
            Start with a free consultation. We'll review your credit and tell you exactly what you need — no pressure, no sales.
          </p>
          <Link to="/booking" className="btn-gold" data-testid="svc-bottom-cta">
            Book My Free Consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
